import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { LazyFallback } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { WarehouseZoneTag } from "@msuite/ui/tag";
import { getWarehouseLocationNumberSegmentString } from "@msuite/utils/stock";
import { guardString } from "@msuite/utils/strings";
import { lazy, Suspense } from "react";
import { useWarehouseZone } from "@/hooks/use-warehouse-zone";
import type { WarehouseZonePanelBaseProps } from "../shared";
import { WarehouseZoneLabel } from "./warehouse-zone-label";

const Notes = lazy(() => import("@/components/notes"));

/**
 * What one hall is, its printed label, and what the warehouse has written about it.
 *
 * The narrow column carries the facts and the label, which is the same shape a transfer carrier's
 * page takes and for the same reason: both are physical things whose page exists partly to produce
 * the plate that goes on them. A zone's plate is the load-bearing one of the two — a transport leg
 * cannot be moved without scanning it.
 *
 * The note thread takes the wider column because it is the part that grows, and a hall accumulates
 * more than most things in the warehouse do: a bay taken out of use while a rack is repaired, a
 * door that ices up, an aisle nobody can get a forklift down. None of that is a column on
 * `warehouseZones` and none of it should be — it is prose about a place.
 *
 * The shelves are a tab of their own rather than a third column, because they are a table somebody
 * works through and want the width.
 *
 * `parent` is the zone itself. Reading and writing a note follow the parent's own read right, and
 * `warehouseZoneNotes` answers to `Stock` — one right over the whole warehouse, with no reach that
 * narrows it to a hall, so the row adds nothing the category did not already decide. It is passed
 * anyway, because leaving it out is what makes a narrower rule silently unanswerable the day one is
 * added.
 */
export function WarehouseZoneGeneral({ warehouseZoneId }: WarehouseZonePanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { warehouseZone } = useWarehouseZone(warehouseZoneId);

	/** Render */
	return (
		<Panels>
			<Panels.Panel flex={1}>
				<ContentCard
					title={t("general.general")}
					icon={icon("Warehouse")}
				>
					<DataGrid>
						<DataGrid.Row>
							<DataGrid.Label>{t("stock.warehouse_zone_number")}</DataGrid.Label>
							<DataGrid.Value>
								{getWarehouseLocationNumberSegmentString(warehouseZone?.number)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("general.name")}</DataGrid.Label>
							<DataGrid.Value>{guardString(warehouseZone?.name)}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("general.type")}</DataGrid.Label>
							<DataGrid.Value>
								{warehouseZone ? (
									<WarehouseZoneTag
										size="md"
										type={warehouseZone.type}
									/>
								) : null}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("general.description")}</DataGrid.Label>
							<DataGrid.Value>{guardString(warehouseZone?.description)}</DataGrid.Value>
						</DataGrid.Row>
					</DataGrid>
				</ContentCard>
				<WarehouseZoneLabel warehouseZoneId={warehouseZoneId} />
			</Panels.Panel>
			<Panels.Panel flex={2}>
				<Suspense fallback={<LazyFallback variant="inline" />}>
					<Notes
						notesTable="warehouseZoneNotes"
						identifier={warehouseZoneId}
						parent={warehouseZone}
					/>
				</Suspense>
			</Panels.Panel>
		</Panels>
	);
}
